import {
  SITE_URL,
  HOME_HERO_IMAGE,
  FUYOUHIN_HERO_IMAGE,
  IHIN_HERO_IMAGE,
  GOMIYASHIKI_HERO_IMAGE,
  normalizePath,
} from "@/seo/config";
import {
  homeFaqs,
  fuyouhinFaqs,
  ihinFaqs,
  gomiYashikiFaqs,
  type FaqItem,
} from "@/data/faqs";

const openingHoursSpecification = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    opens: "09:00",
    closes: "17:00",
  },
];

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "京都ゴミ処分センター",
  url: `${SITE_URL}/`,
  image: HOME_HERO_IMAGE,
  description:
    "京都府全域対応の不用品回収・遺品整理・ゴミ屋敷清掃・残置物撤去・産廃回収サービス。即日対応、法人・不動産会社の案件も歓迎。",
  address: {
    "@type": "PostalAddress",
    addressCountry: "JP",
    addressRegion: "京都府",
    addressLocality: "京都市",
    postalCode: "612-8449",
    streetAddress: "伏見区竹田西小屋ノ内町13",
  },
  areaServed: "京都府",
  telephone: "+81-120-024747",
  openingHoursSpecification,
  priceRange: "¥¥",
};

export const fuyouhinServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "京都の不用品回収・粗大ゴミ処分",
  serviceType: "不用品回収・粗大ゴミ処分",
  url: `${SITE_URL}/fuyouhin-kaisyu/`,
  image: FUYOUHIN_HERO_IMAGE,
  description:
    "京都府全域の不用品回収・粗大ゴミ処分。家具・家電1点からお部屋丸ごとまで最短即日対応、見積無料。分別・搬出込みで対応。",
  areaServed: {
    "@type": "AdministrativeArea",
    name: "京都府",
  },
  provider: {
    "@type": "LocalBusiness",
    name: "京都ゴミ処分センター",
    telephone: "+81-120-024747",
    address: {
      "@type": "PostalAddress",
      addressCountry: "JP",
      addressRegion: "京都府",
      addressLocality: "京都市",
    },
  },
  offers: {
    "@type": "Offer",
    priceCurrency: "JPY",
    priceSpecification: {
      "@type": "PriceSpecification",
      priceCurrency: "JPY",
      minPrice: "9000",
    },
  },
};

export const ihinServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "京都の遺品整理",
  serviceType: "遺品整理",
  url: `${SITE_URL}/ihin-seiri/`,
  image: IHIN_HERO_IMAGE,
  description:
    "京都府全域の遺品整理。遺品整理士在籍。貴重品・思い出の品の仕分け、供養、形見分けまで一貫サポート。見積無料。",
  areaServed: {
    "@type": "AdministrativeArea",
    name: "京都府",
  },
  provider: {
    "@type": "LocalBusiness",
    name: "京都ゴミ処分センター",
    telephone: "+81-120-024747",
    address: {
      "@type": "PostalAddress",
      addressCountry: "JP",
      addressRegion: "京都府",
      addressLocality: "京都市",
    },
  },
  offers: {
    "@type": "Offer",
    priceCurrency: "JPY",
    priceSpecification: {
      "@type": "PriceSpecification",
      priceCurrency: "JPY",
      minPrice: "20000",
    },
  },
};

export const gomiYashikiServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "京都のゴミ屋敷清掃",
  serviceType: "ゴミ屋敷清掃",
  url: `${SITE_URL}/gomi-yashiki/`,
  image: GOMIYASHIKI_HERO_IMAGE,
  description:
    "京都府全域のゴミ屋敷清掃・ゴミ屋敷片付け。堆積ゴミの搬出・消臭・除菌・害虫駆除まで一気に対応。近隣配慮・プライバシー厳守。見積無料。",
  areaServed: {
    "@type": "AdministrativeArea",
    name: "京都府",
  },
  provider: {
    "@type": "LocalBusiness",
    name: "京都ゴミ処分センター",
    telephone: "+81-120-024747",
    address: {
      "@type": "PostalAddress",
      addressCountry: "JP",
      addressRegion: "京都府",
      addressLocality: "京都市",
    },
  },
  offers: {
    "@type": "Offer",
    priceCurrency: "JPY",
    priceSpecification: {
      "@type": "PriceSpecification",
      priceCurrency: "JPY",
      minPrice: "50000",
    },
  },
};

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function faqPageSchema(faqs: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

export function getSchemasForRoute(
  pathname: string,
): Record<string, unknown>[] {
  const path = normalizePath(pathname);

  if (path === "/fuyouhin-kaisyu") {
    return [
      fuyouhinServiceSchema,
      faqPageSchema(fuyouhinFaqs),
      breadcrumbSchema([
        { name: "ホーム", url: `${SITE_URL}/` },
        { name: "不用品回収", url: `${SITE_URL}/fuyouhin-kaisyu/` },
      ]),
    ];
  }

  if (path === "/ihin-seiri") {
    return [
      ihinServiceSchema,
      faqPageSchema(ihinFaqs),
      breadcrumbSchema([
        { name: "ホーム", url: `${SITE_URL}/` },
        { name: "遺品整理", url: `${SITE_URL}/ihin-seiri/` },
      ]),
    ];
  }

  if (path === "/gomi-yashiki") {
    return [
      gomiYashikiServiceSchema,
      faqPageSchema(gomiYashikiFaqs),
      breadcrumbSchema([
        { name: "ホーム", url: `${SITE_URL}/` },
        { name: "ゴミ屋敷清掃", url: `${SITE_URL}/gomi-yashiki/` },
      ]),
    ];
  }

  return [localBusinessSchema, faqPageSchema(homeFaqs)];
}